import { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import { useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { billsApi } from '@/api/bills-api'
import { useToast } from '@/hooks/use-toast'
import { getOverallImpact, getFilterScoreConfig } from '@/utils/monitor-analysis'
import { useConfig } from '@/hooks/config/useConfig'
import type { Bill, BillImpactScore, BillAction, BillsResponse } from '@/types/bill'
import type { APISession } from '@/types/session'
import { getJobByContext, JobContext } from '@umd-dit/jobs-component-react'
import { JobStatus } from '@/types/job'
import { useAuth } from '@/context/AuthContext'

// Define sorting related types
export type SortField = 'impact' | 'bill_number' | 'title' | 'last_modified' | 'crossfile_bill_number' | null
export type SortDirection = 'asc' | 'desc'

interface BillsWorkflowReturn {
  bills: Bill[]
  isLoading: boolean
  isError: boolean
  error: string | null
  selectedBills: string[]
  min_impact_score: BillImpactScore
  selectedSession: APISession
  setSelectedSession: (session: APISession) => void
  setMinImpactScore: (score: BillImpactScore) => void
  handleSelectBill: (billId: string) => void
  handleSelectAll: (bills: Bill[]) => void
  processBillAction: (billIds: string[], action: BillAction) => Promise<number>
  currentPage: number
  pageSize: number
  totalPages: number
  handlePageChange: (page: number) => void
  searchQuery: string
  setSearchQuery: (query: string) => void
  totalFilteredItems: number
  isFetchingNextPage: boolean
  processingActions: Record<string, BillAction>
  sortField: SortField
  sortDirection: SortDirection
  setSorting: (field: SortField, direction: SortDirection) => void
}

export const useBillsWorkflow = (
  initialMinImpactScore: BillImpactScore = 0,
  initialSession: APISession
): BillsWorkflowReturn => {
  const { toast } = useToast()
  const queryClient = useQueryClient()
  const { selectedOrganizationId, selectedWorkspaceId } = useAuth()
  const { config } = useConfig()
  const [min_impact_score, setMinImpactScore] = useState<BillImpactScore>(initialMinImpactScore)
  const [selectedBills, setSelectedBills] = useState<string[]>([])
  const [selectedSession, setSelectedSession] = useState<APISession>(initialSession)
  const [currentPage, setCurrentPage] = useState(1)
  const [searchQuery, setSearchQuery] = useState("")
  const [processingActions, setProcessingActions] = useState<Record<string, BillAction>>({})
  const [sortField, setSortField] = useState<SortField>('impact')
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc')
  const pageSize = 20 // Fixed page size

  useEffect(() => {
    setSelectedBills([])
  }, [min_impact_score, selectedSession, selectedWorkspaceId])

  // Reset to first page when search query changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery])

  // Function to handle sorting
  const setSorting = useCallback((field: SortField, direction: SortDirection) => {
    setSortField(field)
    setSortDirection(direction)
    setCurrentPage(1) // Reset to first page when sorting changes
  }, [])

  // Calculate impact value for sorting
  const getImpactValue = useCallback((bill: Bill): number => {
    const { filterScoreSection, filterScoreField } = getFilterScoreConfig(config)
    const impact = getOverallImpact(bill.monitor_analysis, filterScoreSection, filterScoreField)
    if (!impact) return -999 // No analysis should sort to the end
    
    // Create a numeric value for sorting that prioritizes type first, then score
    const typeValue = { positive: 1000, neutral: 0, negative: -1000 }
    return typeValue[impact.type] + impact.score
  }, [config])

  // Sort bills based on current sort field and direction
  const sortBills = useCallback((bills: Bill[]): Bill[] => {
    if (!sortField) return bills

    return [...bills].sort((a, b) => {
      let comparison = 0

      if (sortField === 'impact') {
        const valueA = getImpactValue(a)
        const valueB = getImpactValue(b)
        comparison = valueB - valueA // Default descending for impact
      } else if (sortField === 'bill_number') {
        comparison = a.bill_number.localeCompare(b.bill_number)
      } else if (sortField === 'title') {
        comparison = (a.title || '').localeCompare(b.title || '')
      } else if (sortField === 'last_modified') {
        comparison = a.last_modified.localeCompare(b.last_modified)
      } else if (sortField === 'crossfile_bill_number') {
        // Handle null values for crossfile_bill_number
        if (!a.crossfile_bill_number && !b.crossfile_bill_number) {
          comparison = 0
        } else if (!a.crossfile_bill_number) {
          comparison = 1 // Sort nulls to the end
        } else if (!b.crossfile_bill_number) {
          comparison = -1 // Sort nulls to the end
        } else {
          comparison = a.crossfile_bill_number.localeCompare(b.crossfile_bill_number)
        }
      }

      // Apply sort direction
      return sortDirection === 'asc' ? comparison : -comparison
    })
  }, [sortField, sortDirection, getImpactValue])


  const billsQuery = useInfiniteQuery({
    queryKey: ['bills', selectedSession, min_impact_score, selectedOrganizationId, selectedWorkspaceId],
    queryFn: async ({ pageParam }: { pageParam: unknown }) => {
      console.log('[useBillsWorkflow] Fetching bills:', {
        session: selectedSession?.toString(),
        min_impact_score: min_impact_score,
        workspace: `${selectedOrganizationId}/${selectedWorkspaceId}`
      });


      if (!selectedOrganizationId || !selectedWorkspaceId) {
        console.warn('[useBillsWorkflow] No workspace selected');
        throw new Error('No workspace selected');
      }

      // Use impact score filtering instead of workflow status
      const response = await billsApi.listBills({
        session: selectedSession,
        min_impact_score: min_impact_score,
        next_token: pageParam as string | undefined
      })

      console.log('[useBillsWorkflow] Fetched', response.items.length, 'bills');
      return response
    },
    initialPageParam: null,
    getNextPageParam: (lastPage) => lastPage.next_token,
    enabled: !!selectedOrganizationId && !!selectedWorkspaceId,
    refetchOnWindowFocus: false,
    staleTime: 300000,
    gcTime: 3600000
  })

  // Automatically fetch pages until we have enough data for the current page
  // This should run only when currentPage changes and is stable
  const stableCurrentPage = useRef(currentPage);
  
  useEffect(() => {
    // Only execute when the page actually changes
    if (stableCurrentPage.current !== currentPage) {
      stableCurrentPage.current = currentPage;
      
      const pagesNeeded = currentPage;
      const pagesAvailable = billsQuery.data?.pages.length || 0;
      
      if (pagesAvailable < pagesNeeded && billsQuery.hasNextPage && !billsQuery.isFetchingNextPage) {
        console.log(`[useBillsWorkflow] Fetching more data for page ${currentPage}`);
        billsQuery.fetchNextPage();
      }
    }
  }, [currentPage, billsQuery])

  // Get all bills from the query
  // The backend API already filters by min_impact_score since we include it in the queryKey
  const allBills = useMemo(() => {
    return billsQuery.data?.pages.flatMap(page => page.items) || []
  }, [billsQuery.data])

  // Filter bills only based on search query
  const filteredBills = useMemo(() => {
    if (!searchQuery.trim()) return allBills;

    const query = searchQuery.trim().toUpperCase();
    return allBills.filter(bill => {
      const bill_number = bill.bill_number || '';
      return bill_number.startsWith(query);
    });
  }, [allBills, searchQuery])

  // Apply sorting to filtered bills
  const sortedBills = useMemo(() => {
    return sortBills(filteredBills)
  }, [filteredBills, sortBills])

  // Get bills for the current page
  const getCurrentPageBills = (): Bill[] => {
    const startIdx = (currentPage - 1) * pageSize
    const endIdx = startIdx + pageSize
    const result = sortedBills.slice(startIdx, endIdx)

    console.log(`[useBillsWorkflow] getCurrentPageBills: Returning ${result.length} bills for page ${currentPage} (${startIdx}-${endIdx}) from ${sortedBills.length} total bills`)

    return result
  }

  const totalItems = sortedBills.length
  const totalPages = Math.ceil(totalItems / pageSize)

  // Ensure current page is valid after filtering
  useEffect(() => {
    if (currentPage > totalPages && totalPages > 0) {
      setCurrentPage(totalPages);
    }
  }, [totalPages, currentPage])

  useEffect(() => {
    if (billsQuery.hasNextPage && !billsQuery.isFetchingNextPage) {
      billsQuery.fetchNextPage();
    }
  }, [billsQuery])

  // Removed updateBillsWorkflowMutation - no longer using workflow status

  const processBillActionMutation = useMutation({
    mutationFn: async ({ bill_numbers, action }: { bill_numbers: string[], action: BillAction }) => {
      if (!selectedOrganizationId || !selectedWorkspaceId) throw new Error('No workspace selected')

      const results = await Promise.all(
        bill_numbers.map(bill_number =>
          billsApi.processBillAction({
            session: selectedSession,
            bill_number: bill_number,
            action: action
          })
        )
      )
      bill_numbers.forEach(bill_number => {
        setProcessingActions(prev => ({
          ...prev,
          [bill_number]: action
        }))
      })
      return {
        bill_numbers,
        action,
        jobIds: results.map(r => r.job_id)
      }
    },
    onSuccess: ({ bill_numbers, action, jobIds }) => {
      bill_numbers.forEach((bill_number, index) => {
        const jobId = jobIds[index]
        if (!jobId) return

        // Add a delay before starting to poll to give the job time to be created
        setTimeout(() => {
          const intervalId = setInterval(async () => {
            try {
              // Create job context with workspace information
              const jobContext: JobContext = {
                app_id: "legislative-review",
                org_id: selectedOrganizationId || undefined,
                ws_id: selectedWorkspaceId || undefined
              }
              console.log('Using context ' + JSON.stringify(jobContext))

              // Use the workspace-aware job polling
              const jobStatus = await getJobByContext(jobContext, jobId)

              // If job is complete or errored, stop polling
              if (jobStatus.status === JobStatus.COMPLETED || jobStatus.status === JobStatus.ERROR) {
                clearInterval(intervalId)

                setProcessingActions(prev => {
                  const newState = { ...prev }
                  delete newState[bill_number]
                  return newState
                })

                if (jobStatus.status === JobStatus.COMPLETED) {
                  console.log(`[useBillsWorkflow] Job completed for bill ${bill_number}`)
                  
                  try {
                    // Fetch the updated bill
                    const bill = await billsApi.getBill(selectedSession, bill_number)
                    console.log(`[useBillsWorkflow] Updated bill data:`, {
                      bill_number: bill.bill_number,
                      impact_score: bill.impact_score,
                      hasMonitorAnalysis: !!bill.monitor_analysis,
                      hasLegislativeReview: !!bill.legislative_review_s3_key
                    })

                    // Update the cache with the new bill data
                    // Since we're using impact score filtering, we need to update the appropriate cache
                    const queryKey = ['bills', selectedSession, min_impact_score, selectedOrganizationId, selectedWorkspaceId]
                    
                    queryClient.setQueryData(queryKey, (oldData: { pages?: Array<BillsResponse> }) => {
                      if (!oldData?.pages) return oldData
                      
                      const updatedData = {
                        ...oldData,
                        pages: oldData.pages.map((page: BillsResponse) => ({
                          ...page,
                          items: page.items.map((item: Bill) => {
                            if (item.bill_number === bill_number) {
                              return bill
                            }
                            return item
                          })
                        }))
                      }
                      
                      return updatedData
                    })
                    
                    console.log(`[useBillsWorkflow] Updated bill ${bill_number} in cache`)
                  } catch (error) {
                    console.error(`[useBillsWorkflow] Error updating bill cache for ${bill_number}:`, error)
                    // Fallback: invalidate cache to ensure consistency
                    queryClient.invalidateQueries({
                      queryKey: ['bills', selectedSession, min_impact_score, selectedOrganizationId, selectedWorkspaceId],
                      exact: true
                    })
                  }
                } else if (jobStatus.status === JobStatus.ERROR) {
                  toast({
                    title: "Processing Failed",
                    description: `Failed to process bill ${bill_number}`,
                    variant: "destructive"
                  })
                }
              }
            } catch (error) {
              console.error('Error polling for job status:', error)
            }
          }, 2000)

          // Clear interval after 5 minutes to prevent infinite polling
          setTimeout(() => {
            clearInterval(intervalId)
            setProcessingActions(prev => {
            const newState = { ...prev }
            delete newState[bill_number]
            return newState
          })
        }, 300000)}, 3000); // Initial delay of 3 seconds before starting to poll
      })

      setSelectedBills([])
      toast({
        title: "Processing Started",
        description: `AI ${action} will be generated for ${bill_numbers.length} bill${bill_numbers.length === 1 ? '' : 's'}`
      })
    },
    onError: (error) => {
      console.error('Failed to process bill action:', error)
      toast({
        title: "Error",
        description: "Failed to start bill processing",
        variant: "destructive"
      })
    }
  })

  const handleSelectBill = useCallback((billId: string) => {
    setSelectedBills(prev => 
      prev.includes(billId) 
        ? prev.filter(id => id !== billId)
        : [...prev, billId]
    )
  }, [])

  const handleSelectAll = useCallback((bills: Bill[]) => {
    setSelectedBills(prev => 
      prev.length === bills.length 
        ? []
        : bills.map(b => b.bill_number)
    )
  }, [])

  const processBillAction = async (bill_numbers: string[], action: BillAction) => {
    await processBillActionMutation.mutateAsync({ bill_numbers, action })
    return bill_numbers.length
  }

  // Use a more comprehensive loading state that includes both initial loading and fetching additional pages
  const isLoading = billsQuery.isPending || billsQuery.isFetching

  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  }

  // Generate the final bills to return
  const billsToReturn = getCurrentPageBills()

  // Debugging log to track what's being returned
  console.log(`[useBillsWorkflow] Returning hook data:`, {
    billsCount: billsToReturn.length,
    isLoading,
    isError: billsQuery.isError,
    min_impact_score,
    totalItems,
    currentPage,
    totalPages,
    processingActions,
    processingActionsKeys: Object.keys(processingActions)
  })

  return {
    bills: billsToReturn,
    isLoading,
    isError: billsQuery.isError,
    error: billsQuery.isError ? 'Failed to load bills' : null,
    selectedBills,
    min_impact_score,
    selectedSession,
    setSelectedSession,
    setMinImpactScore,
    handleSelectBill,
    handleSelectAll,
    processBillAction,
    currentPage,
    pageSize,
    totalPages,
    handlePageChange,
    searchQuery,
    setSearchQuery,
    totalFilteredItems: totalItems,
    isFetchingNextPage: billsQuery.isFetchingNextPage,
    processingActions,
    sortField,
    sortDirection,
    setSorting
  }
}